import {Subject} from 'rxjs';
import {sameDay, toInterfaceArray} from '../helpers/helpers';
import {Food, Unit} from './food';
import {FoodLog, FoodRecord, FoodType, IFoodLog, IFoodRecord} from './FoodRecord';
import {MealType} from './Meal';
import {MealItem, MealItemType} from './MealItem';
import {Recipe} from './Recipe';

// for both last-used amount/type + frequently used tracking
export class FoodHistory {
  // ChangeTracking
  public changedSubject: Subject<FoodHistoryChangedEvent> = new Subject<FoodHistoryChangedEvent>();
  private readonly _records: FoodRecord[];

  constructor(i?: IFoodHistory) {
    this._records = [];
    if (i) {
      if (i.records !== undefined) {
        i.records.forEach(record => {
          this._records.push(new FoodRecord(record));
        });
      }
    }
  }

  get records(): FoodRecord[] {
    const ret: FoodRecord[] = [];
    this._records.forEach(record => {
      ret.push(new FoodRecord(record));
    });
    return ret;
  }

  get allFoods(): string[] { // returns all food & recipes IDs
    const ret: string[] = [];
    this.records.forEach(record => {
      ret.push(record.id.toString());
    });
    return ret;
  }

  get allLogs(): FoodLog[] { // returns ALL individual logs for ALL foods & recipes
    const ret: FoodLog[] = [];
    this.records.forEach(record => {
      record.logs.forEach(log => {
        ret.push(log);
      });
    });
    return ret;
  }

  // method to return single record
  public getRecordByID(id: string) {
    return this.records.find(record => record.id === id);
  }

  public getUsageStatistics(id: string): ItemUsages {
    const record = this.getRecordByID(id);
    if (!record) {
      return new ItemUsages();
    }
    const statistics = new ItemUsages();
    statistics.addUsages(record);
    return statistics;
  }

  // outputs all logs by food & recipe type
  public printAllLogs() {
    console.log('*** ALL LOGS ***');
    this.records.forEach(record => {
      console.log('**** ' + record.name + ',' + record.id);
      record.logs.forEach(log => {
        console.log(log.date.toDateString() + ',' + (log.mealType.toString()) + ',' + log.amount + ',' + log.unit);
      });
    });
  }

  // ***example*** of how the predictions can use the filtering methods below to assign weights
  getPredictions(mealType: MealType, date: Date): FoodRecord[] {
    const ignoreBeforeDays = 30;
    const recentDays = 3;
    const recentWeight = 3;
    const sameWeekDayWeight = 2;
    const sameMealTypeWeight = 3;

    let tempSameWeekDayWeight = sameWeekDayWeight;
    let tempSameMealTypeWeight = sameMealTypeWeight;

    if (mealType === undefined) {
      tempSameMealTypeWeight = 0;
    }
    if (date === undefined) {
      tempSameWeekDayWeight = 0;
      date = new Date();
    }

    this._records.forEach(record => {
      record.weight = record.calculateRank(ignoreBeforeDays, recentDays, recentWeight, date, tempSameWeekDayWeight,
          mealType, tempSameMealTypeWeight);
    });
    return this._records.sort((a, b) => a.weight > b.weight ? -1 : (a.weight < b.weight ? 1 : 0));
  }

  // return most used Foods (count determined by passed in variable)
  // returns id so compatible with food or recipe
  public getOverallMostUsedFoods(count: number): string[] {
    const ret: string[] = [];
    this.records.sort((a, b) => a.totalCount > b.totalCount ? -1 : (a.totalCount < b.totalCount ? 1 : 0));
    for (let i = 0; i < count; i++) {
      if (this.records[i] !== undefined) {
        ret.push(this.records[i].id);
      }
    }
    return ret;
  }

  // weight records by day of week
  public weightRecordsByToday(input: FoodRecord[], weight: number) {
    input.forEach(record => {
      record.weight += record.dayOfWeekCount * weight;
    });
  }

  // weight records by meal type
  public weightRecordsByMeal(input: FoodRecord[], weight: number, mealType: MealType) {
    input.forEach(record => {
      record.weight += record.getMealTypeCount(mealType) * weight;
    });
  }

  // weight records by last x days
  public weightRecordsInLastXDays(input: FoodRecord[], weight: number, xDays: number) {
    input.forEach(record => {
      if (record.daysSinceLastSeen > xDays || record.daysSinceLastSeen === -1) { // -1 returns if never seen
        record.weight += weight;
      }
    });
  }

  // weight records by date range (to be completed)
  public weightRecordsByDateRange(input: FoodRecord[], startDate: Date, endDate: Date): FoodRecord[] {
    const ret: FoodRecord[] = [];
    return ret;
  }

  public logItem(item: MealItem, lastMeal: MealType) {
    let found: FoodRecord;
    const itemId: string = this.getItemId(item);
    this._records.forEach((element, index) => {
      if (element.id === itemId) {
        found = this._records[index];
      }
    });
    const itemAmount: number = this.getItemAmount(item);
    const itemUnit: Unit = this.getItemUnit(item);
    if (found !== undefined) {
      found.addLog(lastMeal, itemAmount, itemUnit);
      this.changedSubject.next(new FoodHistoryChangedEvent(FoodHistoryChangeType.added_food_log,
          found.logs[found.logs.length - 1]));
    } else {
      const itemName: string = this.getItemName(item);
      console.log('first time food record: ' + itemName);
      const record: FoodRecord = new FoodRecord();
      record.id = itemId;
      record.name = itemName;
      record.type = this.getItemType(item);
      record.weight = 0;
      record.addLog(lastMeal, itemAmount, itemUnit);
      this._records.push(record);
      console.log('created :' + record.name + record.id);
      this.changedSubject.next(new FoodHistoryChangedEvent(FoodHistoryChangeType.added_food_record, record));
    }
  }

  public deleteItemLog(item: MealItem, meal: MealType, day: Date): void {
    const record = this._records.find(rec => rec.id === this.getItemId(item));
    if (!record) {
      console.log('No record found for the item which log should be deleted!');
      return;
    }
    const logIndex = this.findItemLogIndex(record, item, meal, day);
    if (logIndex === -1) {
      console.log('No log found which matches the item log which should be deleted!');
      return;
    }
    this.changedSubject.next(new FoodHistoryChangedEvent(FoodHistoryChangeType.deleted_food_log, record.logs[logIndex]));
    record.logs.splice(logIndex, 1);
  }

  public changeItemLog(previousItem: MealItem, newItem: MealItem, meal: MealType, day: Date): void {
    const log = this.findItemLog(previousItem, meal, day);
    if (log === undefined) {
      console.log('No matching log found that could be changed!');
      return;
    }
    const oldLog = log.toInterface();
    log.amount = newItem.amount;
    log.unit = newItem.unit;
    const change: ChangeItemLog = {oldFoodLog: oldLog, newFoodLog: log.toInterface()};
    this.changedSubject.next(new FoodHistoryChangedEvent(FoodHistoryChangeType.changed_food_log, change));
  }

  public changeItemLogByIndex(recordId: string, logIndex: number, newLog: FoodLog): void {
    const record = this._records.find(r => r.id === recordId);
    if (!record) {
      return;
    }
    const log = record.logs[logIndex];
    if (!log) {
      return;
    }
    record.logs[logIndex] = newLog;
  }

  public findItemLog(item: MealItem, meal: MealType, day: Date): FoodLog {
    const record = this._records.find(rec => rec.id === this.getItemId(item));
    if (!record) {
      console.log('No record found for this item!');
      return undefined;
    }
    const logIndex = this.findItemLogIndex(record, item, meal, day);
    if (logIndex === -1) {
      return undefined;
    }
    return record.logs[logIndex];
  }

  // returns food or recipe log from id only
  public getLastLog(id: string) {
    const foodRecord = this.records.find(element => element.id === id);
    if (foodRecord !== undefined) {
      return foodRecord.lastLog;
    } else {
      return undefined;
    }
  }

  public getLastFoodValues(food: Food): FoodLog {
    const foodRecord = this.records.find(element => element.id === food.id);
    return foodRecord.lastLog;
  }

  public getLastRecipeValues(recipe: Recipe): FoodLog {
    const recipeRecord = this.records.find(element => element.id === recipe.id);
    return recipeRecord.lastLog;

  }

  public cleanUpFoodHistory(): void {
    let changed = false;
    let recordsCount = this._records.length;
    for (let i = 0; i < recordsCount - 1; i++) {
      for (let j = i + 1; j < recordsCount; j++) {
        if (this._records[i].id !== this._records[j].id) {
          continue;
        }
        console.log('Found duplicate food history records:', this._records[i], this._records[j]);
        this.mergeRecords(this._records[i], this._records[j]);
        this._records.splice(j, 1);
        recordsCount--;
        j--;
        changed = true;
      }
    }
    if (changed) {
      this.changedSubject.next(new FoodHistoryChangedEvent(FoodHistoryChangeType.updated_food_history, this));
    }
  }

  public toInterface(): IFoodHistory {
    return {records: toInterfaceArray(this.records)};
  }

  private removeWeightZero(input: FoodRecord[]) {
    input.forEach((record, index) => {
      if (record.weight === 0) {
        input.splice(index, 1);
      }
    });
  }

  private weightSort(input: FoodRecord[]) {
    input.sort((a, b) => a.weight > b.weight ? -1 : (a.weight < b.weight ? 1 : 0));
  }

  // generate IDs from results
  private toIDString(records: FoodRecord[]): string[] {
    const ret: string[] = [];
    records.forEach(record => {
      ret.push(record.id.toString());
    });
    return ret;
  }

  private findItemLogIndex(record: FoodRecord, item: MealItem, meal: MealType, day: Date): number {
    let logIndex: number;
    if (item.type === MealItemType.recipe) {
      logIndex = record.logs.findIndex(log => log.amount === this.getItemAmount(item)
          && log.mealType === meal && sameDay(log.date, day));
    } else {
      logIndex = record.logs.findIndex(log => log.amount === this.getItemAmount(item)
          && log.unit.equals(this.getItemUnit(item)) && log.mealType === meal && sameDay(log.date, day));
    }
    return logIndex;
  }

  private getItemId(item: MealItem): string {
    return item.originalItem.id;
  }

  private getItemAmount(item: MealItem): number {
    return item.amount;
  }

  private getItemUnit(item: MealItem): Unit {
    return item.unit;
  }

  private getItemName(item: MealItem): string {
    return item.name;
  }

  private getItemType(item: MealItem): FoodType {
    switch (item.type) {
      case MealItemType.food: {
        return FoodType.food;
      }
      case MealItemType.recipe: {
        return FoodType.recipe;
      }
      case MealItemType.foodProduct: {
        return FoodType.foodProduct;
      }
    }
  }

  private mergeRecords(recordA: FoodRecord, recordB: FoodRecord): void {
    for (const foodLog of recordB.logs) {
      const isDuplicate = recordA.logs.find(l => l.equals(foodLog));
      if (isDuplicate) {
        console.log('Found duplicate food log:', foodLog, isDuplicate);
        continue;
      }
      recordA.logs.push(new FoodLog(foodLog.toInterface()));
    }
  }
}

export class FoodHistoryChangedEvent {
  type: FoodHistoryChangeType;
  value: any;

  constructor(type: FoodHistoryChangeType, value?: any) {
    this.type = type;
    if (value) {
      this.value = value;
    }
  }
}

export enum FoodHistoryChangeType {
  updated_food_history,
  added_food_record,
  added_food_log,
  changed_food_log,
  deleted_food_log
}

export interface IFoodHistory {
  records: IFoodRecord[];
}

export interface ChangeItemLog {
  oldFoodLog: IFoodLog;
  newFoodLog: IFoodLog;
}

export class ItemUsages {
  usages = 0;
  totalAmount = 0; // in grams for food and food products, in portions for recipe

  public addUsage(log: FoodLog): void {
    if (!log) {
      return;
    }
    this.usages++;
    this.totalAmount += log.unit.toGram(log.amount);
  }

  public addUsages(record: FoodRecord): void {
    if (!record || !record.logs) {
      return;
    }
    record.logs.forEach(log => this.addUsage(log));
  }

}

export function isChangeItemLog(object: any): object is ChangeItemLog {
  return ('oldFoodLog' in object && 'newFoodLog' in object);
}
